/**
 * Modul 5 - Pipeline Migration Assessment Tool
 * Scannt alle iFlows eines Tenants und bewertet Migrations-Aufwand zum Pipeline-Konzept
 */
import { useState } from "react";
import { useTranslation } from "react-i18next";
import MuiButton from "@mui/material/Button";
import MuiAlert from "@mui/material/Alert";
import LinearProgress from "@mui/material/LinearProgress";
import { FONT, MONO } from "../theme/tokens";
import { api, v } from "../utils/helpers";

// key ist der Wert (Filter, Export, Vergleich) - die Beschriftung kommt aus dem Katalog
const BUCKETS = [
  { key: "pipeline",       min: 70,  color: "#7c6fe0" },
  { key: "likelyPipeline", min: 40,  color: "var(--accent)" },
  { key: "fragment",       min: 15,  color: "var(--warning)" },
  { key: "standard",       min: 0,   color: "var(--text-3)" },
];
const STAGE_COLORS = {
  generic: "#7c6fe0", sai: "var(--accent)", rai: "var(--danger)",
  mapping: "var(--success)", scenario: "#7c6fe0", standard: "var(--text-3)", unknown: "var(--text-3)",
};
const STAGE_LABELS = {
  generic: "Generic Step", sai: "SAI", rai: "RAI",
  mapping: "Mapping", scenario: "Szenario", standard: "Standard", unknown: "-",
};

function getBucket(score) {
  if (score >= 70) return "pipeline";
  if (score >= 40) return "likelyPipeline";
  if (score >= 15) return "fragment";
  return "standard";
}

function getMigrationRecommendation(result, t) {
  const { score, iflowName } = result;
  if (score.stage === "generic" || score.stage === "sai" || score.stage === "rai") {
    return { text: t("assessment.empfehlung.imKonzept"), type: "ok" };
  }
  if (score.stage === "scenario") {
    return { text: t("assessment.empfehlung.szenario"), type: "info" };
  }
  if (score.total >= 40) {
    return { text: t("assessment.empfehlung.abbildbar"), type: "warn" };
  }
  if (score.total >= 15) {
    return { text: t("assessment.empfehlung.teilweise"), type: "warn" };
  }
  return { text: t("assessment.empfehlung.keins"), type: "neutral" };
}

export default function PipelineAssessment({ instance }) {
  const { t, i18n } = useTranslation("pipeline");
  const gebiet = i18n.language === "en" ? "en-GB" : "de-DE";
  const [step, setStep] = useState(0); // 0=start, 1=scanning, 2=results
  const [scanMode, setScanMode] = useState("metadata");
  const [progress, setProgress] = useState(0);
  const [results, setResults] = useState(null);
  const [summary, setSummary] = useState(null);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [expanded, setExpanded] = useState(null);
  const [showRecommendations, setShowRecommendations] = useState(false);

  const startScan = async () => {
    setStep(1); setError(null); setProgress(10); setResults(null);
    try {
      // Simulate progress while waiting
      const progressTimer = setInterval(() => setProgress(p => Math.min(p + 5, 90)), 800);
      const data = await api(`/api/${instance.id}/pipeline-analysis`, {
        method: "POST",
        body: JSON.stringify({ scanMode }),
      });
      clearInterval(progressTimer);
      setProgress(100);
      setTimeout(() => {
        setResults(data.results || []);
        setSummary(data.summary || {});
        setStep(2);
      }, 400);
    } catch (e) {
      setError(e.message);
      setStep(0);
    }
  };

  const exportData = (format) => {
    if (!results) return;
    if (format === "json") {
      const blob = new Blob([JSON.stringify({ summary, results }, null, 2)], { type: "application/json" });
      const a = document.createElement("a"); a.href = URL.createObjectURL(blob);
      a.download = `pipeline-assessment-${instance.id}.json`; a.click();
    } else if (format === "csv") {
      const header = "iFlow,Package,Score,Bucket,Stage,Marker\n";
      const rows = results.map(r =>
        `"${r.iflowName}","${r.packageName}",${r.score.total},${getBucket(r.score.total)},${r.score.stage},"${r.score.markers.join("; ")}"`
      ).join("\n");
      const blob = new Blob([header + rows], { type: "text/csv" });
      const a = document.createElement("a"); a.href = URL.createObjectURL(blob);
      a.download = `pipeline-assessment-${instance.id}.csv`; a.click();
    } else if (format === "md") {
      const lines = [
        `# ${t("assessment.md.titel", { instanz: instance.name })}`,
        t("assessment.md.datum", { datum: new Date().toLocaleDateString(gebiet) }),
        t("assessment.md.modus", { modus: scanMode }),
        "",
        `## ${t("assessment.md.zusammenfassung")}`,
        `- ${t("assessment.md.gesamt", { n: summary.total })}`,
        `- ${t("assessment.md.pipeline", { n: summary.pipeline })}`,
        `- ${t("assessment.md.wahrscheinlich", { n: summary.likelyPipeline })}`,
        `- ${t("assessment.md.fragment", { n: summary.fragment })}`,
        `- ${t("assessment.md.standard", { n: summary.standard })}`,
        "",
        `## ${t("assessment.md.ergebnisse")}`,
        t("assessment.md.tabellenkopf"),
        "|---|---|---|---|---|---|",
        ...results.map(r => `| ${r.iflowName} | ${r.packageName} | ${r.score.total} | ${getBucket(r.score.total)} | ${r.score.stage} | ${r.score.markers.join(", ")} |`)
      ];
      const blob = new Blob([lines.join("\n")], { type: "text/markdown" });
      const a = document.createElement("a"); a.href = URL.createObjectURL(blob);
      a.download = `pipeline-assessment-${instance.id}.md`; a.click();
    }
  };

  const filtered = (results || []).filter(r => {
    if (filter !== "all" && getBucket(r.score.total) !== filter) return false;
    if (search) {
      const q = search.toLowerCase();
      return r.iflowName.toLowerCase().includes(q) || r.packageName.toLowerCase().includes(q);
    }
    return true;
  });

  // Step 0: Start screen
  if (step === 0) return (
    <div style={{ maxWidth: 640, margin: "0 auto", paddingTop: 32 }}>
      <div style={{ fontSize: 22, fontWeight: 700, fontFamily: FONT, marginBottom: 8 }}>{t("assessment.titel")}</div>
      <div style={{ fontSize: 13, color: v("text-2"), marginBottom: 24 }}>
        {t("assessment.einleitung")}
      </div>
      {error && <MuiAlert severity="error" sx={{ mb: 2 }}>{error}</MuiAlert>}
      <div style={{ background: v("surface-1"), border: `1px solid ${v("border")}`, borderRadius: 10, padding: 20, marginBottom: 20 }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: v("text-1"), marginBottom: 12 }}>{t("assessment.modus.titel")}</div>
        <div style={{ display: "flex", gap: 10 }}>
          {[
            { id: "metadata" },
            { id: "full" },
          ].map(m => (
            <button key={m.id} onClick={() => setScanMode(m.id)} style={{
              flex: 1, padding: "12px 14px", borderRadius: 8, cursor: "pointer", textAlign: "left",
              border: `1px solid ${scanMode === m.id ? v("accent") : v("border")}`,
              background: scanMode === m.id ? `${v("accent")}12` : v("surface-2"),
              color: v("text-1"),
            }}>
              <div style={{ fontWeight: 600, fontSize: 13 }}>{t(`assessment.modus.${m.id}.titel`)}</div>
              <div style={{ fontSize: 11, color: v("text-3"), marginTop: 3 }}>{t(`assessment.modus.${m.id}.sub`)}</div>
            </button>
          ))}
        </div>
        {scanMode === "full" && (
          <MuiAlert severity="warning" sx={{ mt: 1.5, fontSize: 12 }}>
            {t("assessment.modus.warnung")}
          </MuiAlert>
        )}
      </div>
      <MuiButton variant="contained" size="large" onClick={startScan} sx={{ fontFamily: FONT }}>
        {t("assessment.starten")}
      </MuiButton>
    </div>
  );

  // Step 1: Scanning
  if (step === 1) return (
    <div style={{ maxWidth: 480, margin: "40px auto", textAlign: "center" }}>
      <div style={{ fontSize: 16, fontWeight: 600, fontFamily: FONT, marginBottom: 8 }}>
        {scanMode === "full" ? t("assessment.lauf.voll") : t("assessment.lauf.meta")}
      </div>
      <div style={{ fontSize: 12, color: v("text-3"), marginBottom: 20 }}>{t("assessment.lauf.sub")}</div>
      <LinearProgress variant="determinate" value={progress} sx={{ borderRadius: 4, height: 6, mb: 1 }} />
      <div style={{ fontSize: 11, color: v("text-3"), fontFamily: MONO }}>{progress}%</div>
    </div>
  );

  // Step 2: Results
  return (
    <div>
      {/* Summary cards */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 8, marginBottom: 20 }}>
        {BUCKETS.map(b => (
          <div key={b.key} onClick={() => setFilter(filter === b.key ? "all" : b.key)} style={{
            padding: "12px 14px", borderRadius: 8, cursor: "pointer",
            border: `1px solid ${filter === b.key ? b.color : v("border")}`,
            background: filter === b.key ? `${b.color}10` : v("surface-1"),
            transition: "all .12s",
          }}>
            <div style={{ fontSize: 22, fontWeight: 700, fontFamily: FONT, color: b.color }}>
              {summary?.[b.key] ?? 0}
            </div>
            <div style={{ fontSize: 11, fontWeight: 600, color: b.color, marginTop: 2 }}>{t(`assessment.klasse.${b.key}.titel`)}</div>
            <div style={{ fontSize: 10, color: v("text-3") }}>{t(`assessment.klasse.${b.key}.sub`)}</div>
          </div>
        ))}
      </div>

      {/* Toolbar */}
      <div style={{ display: "flex", gap: 8, marginBottom: 12, flexWrap: "wrap", alignItems: "center" }}>
        <input value={search} onChange={e => setSearch(e.target.value)} placeholder={t("assessment.suche")}
          style={{ flex: 1, minWidth: 180, background: v("surface-2"), border: `1px solid ${v("border")}`, borderRadius: 6, padding: "6px 10px", color: v("text-1"), fontSize: 12, fontFamily: FONT, outline: "none" }} />
        <button onClick={() => setShowRecommendations(r => !r)} style={{
          padding: "5px 12px", borderRadius: 6, border: `1px solid ${showRecommendations ? v("accent") : v("border")}`,
          background: showRecommendations ? `${v("accent")}12` : "transparent", color: showRecommendations ? v("accent") : v("text-2"),
          fontSize: 11, cursor: "pointer", fontFamily: FONT, fontWeight: 600,
        }}>{t("assessment.empfehlungen")}</button>
        <div style={{ display: "flex", gap: 4, marginLeft: "auto" }}>
          {["json", "csv", "md"].map(fmt => (
            <button key={fmt} onClick={() => exportData(fmt)} style={{
              padding: "5px 10px", borderRadius: 5, border: `1px solid ${v("border")}`,
              background: "transparent", color: v("text-2"), fontSize: 11, cursor: "pointer", fontFamily: MONO,
            }}>.{fmt}</button>
          ))}
        </div>
        <button onClick={() => { setStep(0); setResults(null); }} style={{
          padding: "5px 12px", borderRadius: 6, border: `1px solid ${v("border")}`,
          background: "transparent", color: v("text-3"), fontSize: 11, cursor: "pointer", fontFamily: FONT,
        }}>{t("assessment.neuerScan")}</button>
      </div>

      <div style={{ fontSize: 11, color: v("text-3"), marginBottom: 8 }}>
        {t("assessment.trefferzahl", { gefiltert: filtered.length, gesamt: results.length })}
        {filter !== "all" && t("assessment.filterHinweis", { klasse: t(`assessment.klasse.${filter}.titel`) })}
      </div>

      {/* Results table */}
      <div style={{ borderRadius: 8, border: `1px solid ${v("border")}`, overflow: "hidden" }}>
        <div style={{ display: "grid", gridTemplateColumns: "36px 1fr 120px 80px 90px 70px", padding: "8px 16px", background: v("surface-2"), fontSize: 10, fontWeight: 600, color: v("text-3"), textTransform: "uppercase", letterSpacing: .5 }}>
          <div/><div>{t("assessment.spalte.name")}</div><div>{t("assessment.spalte.stage")}</div><div>{t("assessment.spalte.score")}</div><div>{t("assessment.spalte.klassifikation")}</div><div/>
        </div>
        {filtered.length === 0 && (
          <div style={{ padding: 32, textAlign: "center", color: v("text-3"), fontSize: 13 }}>{t("assessment.leer")}</div>
        )}
        {filtered.map(r => {
          const bucket = getBucket(r.score.total);
          const bInfo = BUCKETS.find(b => b.key === bucket);
          const isExp = expanded === r.iflowId;
          const rec = getMigrationRecommendation(r, t);
          return (
            <div key={r.iflowId}>
              <div onClick={() => setExpanded(isExp ? null : r.iflowId)}
                style={{ display: "grid", gridTemplateColumns: "36px 1fr 120px 80px 90px 70px", padding: "10px 16px", borderTop: `1px solid ${v("border-subtle")}`, alignItems: "center", cursor: "pointer", background: isExp ? v("surface-1") : "transparent", fontSize: 12 }}
                onMouseEnter={e => e.currentTarget.style.background = v("surface-1")}
                onMouseLeave={e => e.currentTarget.style.background = isExp ? v("surface-1") : "transparent"}>
                <div style={{ width: 8, height: 8, borderRadius: "50%", background: bInfo?.color }} />
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{r.iflowName}</div>
                  <div style={{ fontSize: 10, color: v("text-3"), overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{r.packageName}</div>
                </div>
                <span style={{ fontSize: 10, fontWeight: 600, color: STAGE_COLORS[r.score.stage] || v("text-3"), textTransform: "uppercase" }}>{STAGE_LABELS[r.score.stage] || r.score.stage}</span>
                <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                  <div style={{ flex: 1, height: 4, borderRadius: 2, background: v("border"), overflow: "hidden" }}>
                    <div style={{ width: `${r.score.total}%`, height: "100%", background: bInfo?.color, borderRadius: 2, transition: "width .3s" }} />
                  </div>
                  <span style={{ fontFamily: MONO, fontSize: 10, color: bInfo?.color, minWidth: 24, textAlign: "right" }}>{r.score.total}</span>
                </div>
                <span style={{ fontSize: 10, color: bInfo?.color, fontWeight: 600 }}>{t(`assessment.klasse.${bucket}.titel`)}</span>
                <span style={{ fontSize: 10, color: v("text-3") }}>{isExp ? "▲" : "▼"}</span>
              </div>
              {isExp && (
                <div style={{ padding: "12px 16px 16px 52px", background: v("surface-1"), borderTop: `1px solid ${v("border")}` }}>
                  {/* Markers */}
                  <div style={{ marginBottom: 10 }}>
                    <div style={{ fontSize: 10, fontWeight: 600, color: v("text-3"), textTransform: "uppercase", letterSpacing: .5, marginBottom: 4 }}>{t("assessment.marker.titel")}</div>
                    {r.score.markers.length === 0
                      ? <span style={{ fontSize: 11, color: v("text-3") }}>{t("assessment.marker.leer")}</span>
                      : <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
                          {r.score.markers.map((m, i) => (
                            <span key={i} style={{ fontSize: 10, padding: "2px 7px", borderRadius: 4, background: v("surface-0"), border: `1px solid ${v("border")}`, color: v("text-2"), fontFamily: MONO }}>{m}</span>
                          ))}
                        </div>}
                  </div>
                  {/* ProcessDirect addresses */}
                  {r.processDirect?.length > 0 && (
                    <div style={{ marginBottom: 10 }}>
                      <div style={{ fontSize: 10, fontWeight: 600, color: v("text-3"), textTransform: "uppercase", letterSpacing: .5, marginBottom: 4 }}>{t("assessment.processDirect")}</div>
                      {r.processDirect.map((addr, i) => (
                        <div key={i} style={{ fontSize: 11, fontFamily: MONO, color: "var(--accent)", padding: "2px 0" }}>{addr}</div>
                      ))}
                    </div>
                  )}
                  {/* Recommendation */}
                  {showRecommendations && (
                    <div style={{
                      padding: "8px 12px", borderRadius: 6, fontSize: 12,
                      background: rec.type === "ok" ? `${v("success")}10` : rec.type === "warn" ? `${v("warning")}10` : v("surface-2"),
                      border: `1px solid ${rec.type === "ok" ? v("success") : rec.type === "warn" ? v("warning") : v("border")}20`,
                      color: rec.type === "ok" ? v("success") : rec.type === "warn" ? v("warning") : v("text-2"),
                    }}>
                      {rec.text}
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
